<template>

    <WizardViewTemplate :rows="sessionRows" :loading="loading" :createModel="createModel" :create="startSession"
        :update="update">

        <!-- OVERVIEW -->

        <template #overview="{ rows, loading, select }">

            <div v-if="loading">Loading Sessions...</div>

            <div v-else-if="!sessionRows.length">
                No sessions found.
            </div>

            <div v-else>
                <Table :cols="columns" :rows="sessionRows" @select="select" />
            </div>

        </template>

        <!-- CREATE -->

        <template #create>

            <div v-if="!thisSessionKey">

                <p>Start a new wizard session</p>

                <button class="sdev-button sdev-bg-surface-elevated" @click="startSession">
                    Start Wizard
                </button>

            </div>

            <div v-else>

                <WizardStep :step="step" :data="templateData" :templates="templates" :identities="identities"
                    :context="wizardContext" :preview="preview" :previewLoading="isPreviewLoading"
                    :totalStepsInFlow="totalStepsInFlow" :actualStepInFlow="actualStepInFlow" @next="handleNext"
                    @start="startWizard" @preview="loadPreview" @execute="executeWizard" />

            </div>

        </template>

        <!-- INSPECT -->

        <template #inspect>

            <div v-if="!preview">
                Run the wizard to preview the plan.
            </div>

            <div v-else>

                <h3>Identities</h3>

                <ul>
                    <li v-for="i in preview.identities" :key="i.key">
                        {{ i.key }} — {{ i.status }}
                    </li>
                </ul>

                <h3>Relations</h3>

                <ul>
                    <li v-for="r in preview.relations" :key="r.parent + r.child">
                        {{ r.parent }} → {{ r.child }} ({{ r.type }})
                    </li>
                </ul>

                <h3>Secrets</h3>

                <ul>
                    <li v-for="s in preview.secrets" :key="s.identity + s.type">
                        {{ s.identity }} — {{ s.type }}
                    </li>
                </ul>

            </div>

        </template>

        <!-- HISTORY -->

        <template #history>

            <div>
                Wizard execution history (future feature)
            </div>

        </template>

    </WizardViewTemplate>

</template>
<script setup>

import { ref, onMounted } from "vue"

import {
    wizardSessions,
    wizardStart,
    wizardStep,
    wizardFinalize,
    loadIdentities
} from "@/domain/wizard/api.wizard"

import { resolveWizardPlan } from "@/domain/wizard/wizard.plan.resolver"

const sessionRows = ref([])
const columns = ref([])
const loading = ref(false)

const identities = ref([])
const templates = ref([])

const thisSessionKey = ref(null)

const step = ref(null)
const templateData = ref(null)

const preview = ref(null)
const isPreviewLoading = ref(false)

const totalStepsInFlow = ref(0)
const actualStepInFlow = ref(0)

const wizardContext = ref({})

const createModel = ref({})

async function loadSessions() {

    loading.value = true

    const res = await wizardSessions()

    columns.value = res.data.columns.map(c => ({
        key: c,
        label: c
    }))

    sessionRows.value = res.data.sessions

    loading.value = false

}

async function loadAssociatedIdentities() {

    const res = await loadIdentities()

    identities.value = res.data.map(o => ({
        key: o.identity_key,
        name: o.identity_name
    }))

}
async function startSession() {

    const res = await wizardStart()

    const startData = res.data

    thisSessionKey.value = startData.session_key
    step.value = startData.step

    templates.value = startData.templates.map(t => {

        const template = JSON.parse(t.identity_name)

        return {
            key: t.identity_key,
            label: template.template_name,
            template
        }

    })

}
async function startWizard(templateKey) {

    const template = templates.value.find(t => t.key === templateKey)

    if (!template) return

    templateData.value = template.template

    totalStepsInFlow.value = templateData.value.wizard.length
    actualStepInFlow.value = 1

    const res = await wizardStep({

        wizard_id: thisSessionKey.value,
        step: "template",

        data: {
            template: templateKey
        }

    })

    step.value = res.data.next_step

}

async function handleNext(formData) {

    const res = await wizardStep({

        wizard_id: thisSessionKey.value,
        step: step.value,
        data: formData

    })

    wizardContext.value = {
        ...wizardContext.value,
        ...formData
    }

    step.value = res.data.next_step

    if (step.value === "finalize") {

        await loadPreview()

        return

    }

    actualStepInFlow.value++

}
async function loadPreview() {

    isPreviewLoading.value = true

    const res = await wizardFinalize({

        wizard_id: thisSessionKey.value,
        dry_run: true

    })

    preview.value = resolveWizardPlan(res)

    isPreviewLoading.value = false

}
async function executeWizard() {

    await wizardFinalize({

        wizard_id: thisSessionKey.value,
        dry_run: false

    })

    preview.value = null
    thisSessionKey.value = null

    loadSessions()

}
onMounted(async () => {

    await loadSessions()
    await loadAssociatedIdentities()

})
</script>